/**
 * Account API client. Wraps the generic apiFetch with typed signatures.
 * Endpoints:
 *   GET    /api/accounts          → list accounts (excludes 'closed')
 *   GET    /api/accounts/:id      → single account incl. validation info
 *   POST   /api/accounts          → create (status='pending')
 *   POST   /api/accounts/:id/validate → trigger ephemeral validation
 *   POST   /api/accounts/:id/close    → mark closed
 */

import type {
  CreateAccountReq,
  AccountRules,
  AccountRulesRes,
  PatchAccountRulesReq,
} from "@pfh/shared";
import { api } from "../api";

/** Row shape returned by the Worker (snake_case from D1). */
export interface AccountRow {
  id: string;
  /** Opaque URL slug. All dashboard links use this in place of `id`
   *  so the raw id (which includes the firm name) never leaks through
   *  the address bar. */
  slug: string;
  /** Opaque slug of the pair this account belongs to (if any). Joined
   *  from the pairs table by the /api/accounts endpoint. */
  pair_slug: string | null;
  firm_id: string;
  platform_type: string;
  adapter: string;
  role: string;
  pair_id: string | null;
  phase: string;
  status:
    | "pending"
    | "validating"
    | "active"
    | "failed_validation"
    | "paused"
    | "closed";
  challenge_fee: number | null;
  login: string | null;
  password: string | null;
  broker_server: string | null;
  login_url: string | null;
  storage_state: string | null;
  account_info: string | null; // JSON string, parsed on demand
  validation_error: string | null;
  validated_at: number | null;
  created_at: number;
  // Rule override columns (migrations 0020 + 0021). All nullable —
  // populated once the user confirms rules via the wizard or the
  // /accounts/:id detail page.
  account_size: number | null;
  steps: number | null;
  challenge_type: string | null;
  daily_loss_pct: number | null;
  daily_dd_type: string | null;
  max_drawdown_pct: number | null;
  max_dd_type: string | null;
  profit_target_pct: number | null;
  profit_target_step1_pct: number | null;
  profit_target_step2_pct: number | null;
  profit_target_step3_pct: number | null;
  profit_split_pct: number | null;
  payout_frequency: string | null;
  leverage: string | null;
  leverage_fx: string | null;
  leverage_indices: string | null;
  leverage_metals: string | null;
  leverage_commodities: string | null;
  leverage_crypto: string | null;
  min_trading_days: number | null;
  weekend_holding_allowed: number | null;
  news_trading_allowed: number | null;
  consistency_rule_pct: number | null;
  // Migration 0054 — instant-funded-leaning rule fields. NULL on rows
  // created before the operator confirmed rules under the new editor.
  max_risk_per_trade_pct: number | null;
  max_lot_size: number | null;
  payout_threshold_pct: number | null;
  min_profitable_days: number | null;
  rules_confirmed_at: number | null;
  rules_edited_at: number | null;
  // Operator-editable nickname (migration 0019). Surfaces in the
  // accounts list + detail header so a parked partner account can be
  // tagged ("loser 2k buffer") and picked up later.
  account_label: string | null;
  // Per-bot account pinning (migration 0029). The bot host responsible
  // for this account's worker. Null when no bot was active at create
  // time — the operator reassigns from the AccountDetail page.
  assigned_bot_id: string | null;
  // Terminal outcome (migration 0040). NULL while the account is still
  // in-flight; set once the operator clicks Mark Passed or Mark Breached.
  // `final_equity` / `final_balance` freeze the last snapshot so the
  // UI keeps meaningful numbers after the bot drains.
  outcome: "passed" | "breached" | null;
  outcome_at: number | null;
  final_equity: number | null;
  final_balance: number | null;
  closed_at: number | null;
  // Per-account ground truth reported by the bot. Sleep INTENT is now
  // pair-level (see account-pairs.ts → setPairMode/sleepPair/wakePair);
  // these two columns just reflect what each leg's worker actually did.
  current_worker_state: "awake" | "asleep" | "unknown";
  current_worker_state_at: number | null;
}

/** One row from GET /api/accounts/:id/trades. */
export interface AccountTrade {
  id: string;
  account_id: string;
  pair_id: string;
  pair_slug: string | null;
  ticket: number;
  symbol: string;
  side: "buy" | "sell";
  lots: number;
  entry_price: number;
  close_price: number | null;
  pnl: number | null;
  opened_at: number;
  closed_at: number | null;
}

/** One sample from GET /api/accounts/:id/snapshots. */
export interface AccountSnapshot {
  equity: number;
  balance: number;
  pnl_open: number;
  snapshot_at: number;
}

/** Decoded account_info JSON shape. */
export interface AccountInfo {
  accountNumber: string;
  server: string;
  accountType: string;
  broker: string;
  currency: string;
  balance: number;
  deposit: number;
  verifiedAt: number;
}

export function decodeAccountInfo(row: AccountRow): AccountInfo | null {
  if (!row.account_info) return null;
  try {
    return JSON.parse(row.account_info) as AccountInfo;
  } catch {
    return null;
  }
}

export async function listAccounts(): Promise<AccountRow[]> {
  const res = await api.get<{ accounts: AccountRow[] }>("/api/accounts");
  return res.accounts;
}

export async function getAccount(id: string): Promise<AccountRow> {
  const res = await api.get<{ account: AccountRow }>(
    `/api/accounts/${encodeURIComponent(id)}`,
  );
  return res.account;
}

export async function createAccount(
  req: CreateAccountReq,
): Promise<{ ok: true; accountId: string; status: "pending" }> {
  return api.post("/api/accounts", req);
}

export async function validateAccount(
  id: string,
): Promise<{ ok: true; accountId: string; status: "validating" }> {
  return api.post(`/api/accounts/${encodeURIComponent(id)}/validate`, {});
}

/**
 * Edit any of the four fields the bot needs to log in. `null` or "" on
 * `loginUrl` clears the per-account override and falls back to the
 * firm-platform terminal_url chain. Fields not in the object are left
 * untouched (worker-side three-state semantics).
 */
export async function patchAccountCredentials(
  id: string,
  patch: {
    loginUrl?: string | null;
    login?: string | null;
    password?: string | null;
    brokerServer?: string | null;
  },
): Promise<{ ok: true; accountId: string }> {
  return api.patch(`/api/accounts/${encodeURIComponent(id)}/credentials`, patch);
}

export async function closeAccount(id: string): Promise<void> {
  await api.post<null>(`/api/accounts/${encodeURIComponent(id)}/close`, {});
}

export async function getAccountTrades(id: string): Promise<AccountTrade[]> {
  const res = await api.get<{ trades: AccountTrade[] }>(
    `/api/accounts/${encodeURIComponent(id)}/trades`,
  );
  return res.trades;
}

export async function getAccountSnapshots(
  id: string,
  opts?: { since?: number; until?: number; limit?: number },
): Promise<AccountSnapshot[]> {
  const params: string[] = [];
  if (opts?.since != null) params.push(`since=${encodeURIComponent(opts.since)}`);
  if (opts?.until != null) params.push(`until=${encodeURIComponent(opts.until)}`);
  if (opts?.limit != null) params.push(`limit=${encodeURIComponent(opts.limit)}`);
  const qs = params.length > 0 ? `?${params.join("&")}` : "";
  const res = await api.get<{ snapshots: AccountSnapshot[] }>(
    `/api/accounts/${encodeURIComponent(id)}/snapshots${qs}`,
  );
  return res.snapshots;
}

/** Aggregated objectives block — see worker route docstring for the
 *  derivation of each field. The chart pulls reference floors from the
 *  same response so the bars and the chart always agree. */
export interface AccountObjectives {
  accountId: string;
  startBalance: number;
  currentEquity: number;
  maxLoss: { used: number; limit: number; remaining: number; pct: number; floor: number };
  dailyLoss: {
    used: number;
    limit: number;
    remaining: number;
    pct: number;
    floor: number;
    /** First-snapshot-of-the-day equity baseline. Exposed so the
     *  dashboard can compute live daily dip = todayAnchor − liveEquity
     *  without re-fetching on every WS tick. */
    todayAnchor: number;
  };
  consistency: {
    mostProfitableDayPnl: number;
    totalProfits: number;
    ratio: number;
    thresholdPct: number;
    /** null when totalProfits <= 0 (rule N/A — can't fail consistency
     *  without any profit yet). */
    passing: boolean | null;
  };
  /** USD equity that hits the firm's profit target, phase-aware. Null
   *  on funded accounts (target already cleared) or when the operator
   *  hasn't captured the firm's target percentage. */
  profitTargetUsd: number | null;
  /** Roadmap to the next withdrawal — turns the firm's static rules
   *  (consistency %, payout threshold, minimum profitable days) into
   *  per-day actionable numbers. All optional fields are null when
   *  the corresponding rule isn't captured on the account row. */
  withdrawalRoadmap: {
    /** Minimum profit (USD) the firm requires before a payout request
     *  can be opened. Derived from `payout_threshold_pct × account_size`. */
    payoutThresholdUsd: number | null;
    realizedProfitUsd: number;
    remainingToPayoutUsd: number | null;
    minProfitableDays: number | null;
    minTradingDays: number | null;
    profitableDaysBanked: number;
    /** `minProfitableDays − profitableDaysBanked`, floored at 0. */
    daysRemainingToPayoutRule: number | null;
    /** Largest single-day net P&L the operator can book TODAY without
     *  tipping the consistency ratio over the threshold. null when
     *  total profit ≤ 0 (rule N/A yet). */
    safeTodayCapUsd: number | null;
    /** Per-day profit needed to clear the payout floor across the
     *  required-days window, clamped to `safeTodayCapUsd` so it never
     *  suggests a breach. null when no payout threshold is configured. */
    dailyTargetUsd: number | null;
    /** Forward projection: at each future total `T*`, the biggest
     *  single-day P&L still safe under the consistency rule
     *  (= threshold·T* / (1−threshold)). 5 rows stepping current
     *  total → payout floor. Empty when threshold ≤ 0 or ≥ 1. */
    headroom: Array<{ totalAtMilestoneUsd: number; maxSafeDayUsd: number }>;
  };
  /** Next UTC midnight, ms epoch. Drives the daily-reset countdown. */
  dailyResetAtMs: number;
}

export async function getAccountObjectives(id: string): Promise<AccountObjectives> {
  return api.get<AccountObjectives>(
    `/api/accounts/${encodeURIComponent(id)}/objectives`,
  );
}

/** POST /api/accounts/:id/mark-passed — terminal, funded-only, idempotent. */
export async function markAccountPassed(id: string): Promise<AccountRow> {
  const res = await api.post<{ account: AccountRow }>(
    `/api/accounts/${encodeURIComponent(id)}/mark-passed`,
    {},
  );
  return res.account;
}

/** POST /api/accounts/:id/mark-breached — terminal, idempotent. */
export async function markAccountBreached(id: string): Promise<AccountRow> {
  const res = await api.post<{ account: AccountRow }>(
    `/api/accounts/${encodeURIComponent(id)}/mark-breached`,
    {},
  );
  return res.account;
}

/**
 * Roll the account forward to the next phase with the firm-issued
 * next-step credentials. Same account id → same profile dir → same
 * fingerprint. Bot reloads on the preserved Brave context.
 */
export type AdvancePhaseBody =
  | {
      phase: "step2" | "funded";
      login: string;
      password: string;
      brokerServer?: string | null;
      loginUrl?: string | null;
    }
  | {
      // Match-Trader path: firm dashboard login is shared across phases.
      // Operator only types the new account number visible on the
      // dashboard's account list — the driver clicks that card.
      phase: "step2" | "funded";
      accountLabel: string;
    };

export async function advanceAccountPhase(
  id: string,
  body: AdvancePhaseBody,
): Promise<{ ok: true; accountId: string; phase: string; status: string }> {
  return api.post(`/api/accounts/${encodeURIComponent(id)}/advance-phase`, body);
}

/** POST /api/accounts/:id/resume — reactivate a paused partner leg. */
export async function resumeAccount(id: string): Promise<{ ok: true }> {
  return api.post(`/api/accounts/${encodeURIComponent(id)}/resume`, {});
}

// Sleep/wake mutations + presence ping live on the pair (the
// account_pairs entity). See apps/dashboard/src/lib/api/account-pairs.ts:
// setPairMode, sleepPair, wakePair, setPairAutoSleepProtected,
// postDashboardPresence.

/**
 * PATCH /api/accounts/:id/label — set or clear the operator nickname.
 * Pass null / empty string to clear.
 */
export async function setAccountLabel(
  id: string,
  label: string | null,
): Promise<{ ok: true; accountLabel: string | null }> {
  return api.patch(`/api/accounts/${encodeURIComponent(id)}/label`, { label });
}

/**
 * POST /api/accounts/:id/refetch-history
 *
 * Kick the assigned bot to scrape the platform's History tab and
 * stream the trades back. Async — returns once the frame is queued;
 * caller polls `getAccountTrades` to pick up the backfilled rows.
 */
export async function refetchAccountHistory(
  id: string,
): Promise<{ ok: true; requestId: string }> {
  return api.post(
    `/api/accounts/${encodeURIComponent(id)}/refetch-history`,
    {},
  );
}

/**
 * Mirrors the worker's AutoPairResult (services/pairs/auto-match.ts).
 * Kept local instead of re-exported from @pfh/shared so the shared
 * package stays centered on Zod schemas — this is a plain response type.
 */
export interface RematchResult {
  paired: boolean;
  pairId: string | null;
  pairSlug: string | null;
  accountPairId: string | null;
  accountPairSlug: string | null;
  partner: string | null;
  proposalSlugs: string[];
  reason: string | null;
}

export async function rematchAccount(id: string): Promise<RematchResult> {
  return api.post<RematchResult>(
    `/api/accounts/${encodeURIComponent(id)}/rematch`,
    {},
  );
}

/**
 * Response from PATCH /api/accounts/:id/rules. `pair` is only set
 * when the body included `confirm:true` AND a compatible partner
 * was found. The wizard's result step reads this to show
 * "auto-paired with X" inline.
 */
export interface PatchRulesRes {
  ok: true;
  updatedAt: number;
  pair: {
    paired: boolean;
    pairId: string | null;
    pairSlug: string | null;
    partner: string | null;
    reason: string | null;
  } | null;
}

export async function getAccountRules(id: string): Promise<AccountRulesRes> {
  return api.get<AccountRulesRes>(`/api/accounts/${encodeURIComponent(id)}/rules`);
}

export async function patchAccountRules(
  id: string,
  body: PatchAccountRulesReq,
): Promise<PatchRulesRes> {
  return api.patch<PatchRulesRes>(`/api/accounts/${encodeURIComponent(id)}/rules`, body);
}

export type { AccountRules, AccountRulesRes, PatchAccountRulesReq };
